import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StartTrialPage() {
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled') === '1';
  const [isRedirecting, setIsRedirecting] = useState(false);

  const startCheckout = useCallback(async () => {
    setIsRedirecting(true);
    try {
      const response = await fetch(`${API}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ origin_url: window.location.origin, plan: 'pastor_pro_trial' })
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Could not start your trial. Please try again.');
        setIsRedirecting(false);
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
      setIsRedirecting(false);
    }
  }, []);

  useEffect(() => {
    // Don't auto-fire a fresh checkout session if we just bounced back from a
    // cancelled one — that would loop the visitor straight back into Stripe.
    if (!cancelled) {
      startCheckout();
    }
  }, [cancelled, startCheckout]);

  return (
    <div className="min-h-screen bg-[#fafaf9] flex flex-col">
      <header className="py-6 px-6">
        <Link to="/" className="font-serif text-xl font-bold text-[#1e3a8a]">
          SermonSift
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="card p-8 text-center animate-fade-in">
            <div className="feature-icon mx-auto mb-6">
              <ShieldCheck className="w-6 h-6" strokeWidth={1.5} />
            </div>

            {cancelled ? (
              <>
                <h1 className="font-serif text-2xl font-bold text-[#1e3a8a] mb-2">
                  Trial setup cancelled
                </h1>
                <p className="text-stone-600 mb-8">
                  No card was charged. You'll need to add a payment method to start your free trial and unlock SermonSift.
                </p>
                <Button
                  className="btn-primary w-full"
                  onClick={startCheckout}
                  disabled={isRedirecting}
                  data-testid="retry-trial-btn"
                >
                  {isRedirecting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Redirecting...
                    </span>
                  ) : 'Try Again'}
                </Button>
              </>
            ) : (
              <>
                <h1 className="font-serif text-2xl font-bold text-[#1e3a8a] mb-2">
                  Setting up your free trial
                </h1>
                <p className="text-stone-600 mb-6">
                  Free for 48 hours, then $9.99/month — cancel anytime before your trial ends and you won't be charged a thing.
                </p>
                <div className="flex items-center justify-center gap-2 text-stone-500 text-sm">
                  <span className="w-4 h-4 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin" />
                  Redirecting you to secure checkout...
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="py-6 text-center text-sm text-stone-400">
        <p>A Bethany Labs product</p>
      </footer>
    </div>
  );
}
